import { HTTP, Maybe, caseOf, filter, fold, map, maybe, some } from '../../lib'

import { config } from '@/lib/helpers'
import httpClient from '../../api/httpClient'
import { Domain } from '../../lib/types/Domain'
import Status from '../../lib/types/Status'
import { Store, makeStore, serializeStore } from '../../lib/types/Store'
import { errorHandler, utmTags } from '../../utils/helper'

const emptyStore = Store(
  -1,
  '',
  '',
  '',
  [],
  [],
  [],
  [Domain(0, Maybe.None(), '')],
  '',
  '',
  '',
  false,
  false,
  null,
  [],
  null,
  [],
  0,
  [],
  1,
  0,
  0,
  0,
  0,
  null,
  null,
  {
    reference: '',
    vat_number: '',
    invoicing_address: [],
    emails: [],
    due_days: 30,
  },
  false,
  false,
  null,
  [],
  false,
)

export default {
  state: {
    selectedStore: HTTP.Request.Inactive(),
    originalStore: emptyStore,
    storeLogoFile: null,
    storeLogo: null,
    status: Status.Idle(),
    storeProviders: [],
    storeCategories: [],
    feedOrUrl: null,
    paymentTypes: [],
    currentStep: 0,
    validationStatus: '',
    errorMessageList: [],
    groups: HTTP.Request.Inactive(),
    utmTags: { ...utmTags },
    showUtmTags: false,
    providersWithUtmTags: ['adtraction', 'awin', 'tradedoubler', 'webgains', 'ga'],
    urlPlaceholder: 'http://clk.tradedoubler.com/click?p(70361)a(2978484)url(:TARGET_ENCODED_URL)',
  },

  mutations: {
    setSelectedStore(state, selectedStore) {
      state.selectedStore = selectedStore
      state.status = Status.Idle()
    },

    setOriginalStore(state, originalStore) {
      state.originalStore = originalStore
    },

    setStoreProviders(state, storeProviders) {
      state.storeProviders = storeProviders
    },

    setStoreCategories(state, storeCategories) {
      state.storeCategories = storeCategories
    },

    resetStore(state) {
      state.selectedStore = HTTP.Request.Done(state.originalStore)
    },

    setField(state, { field, value }) {
      state.selectedStore = map(state.selectedStore, (store) => ({
        ...store,
        [field]: value,
      }))
    },

    updateDomain(state, { id, host }) {
      state.selectedStore = map(state.selectedStore, (store) => ({
        ...store,
        domains: map(store.domains, (domain) => (domain.id === id ? { ...domain, host } : domain)),
      }))
    },

    addDomain(state) {
      state.selectedStore = map(state.selectedStore, (store) => ({
        ...store,
        domains: store.domains.concat(Domain(store.domains.length ? store.domains[store.domains.length - 1].id + 1 : 0, Maybe.None(), '')),
      }))
    },

    removeDomain(state, id) {
      state.selectedStore = map(state.selectedStore, (store) => ({
        ...store,
        domains: filter(store.domains, (domain) => id !== domain.id),
      }))
    },

    setStatus(state, status) {
      state.status = status
    },

    setLogoFile(state, file) {
      state.storeLogoFile = file
    },

    setLogo(state, url) {
      state.storeLogo = url
    },
    clearLogo(state) {
      state.storeLogo = null
      state.storeLogoFile = null
    },
    setFeedOrUrl(state, value) {
      state.feedOrUrl = value
    },
    setPaymentTypes(state, value) {
      if (state.paymentTypes.includes(value)) {
        state.paymentTypes = state.paymentTypes.filter((item) => item !== value)
      } else {
        state.paymentTypes.push(value)
      }
    },
    setEmptyPaymentTypes(state) {
      state.paymentTypes = []
    },
    setCurrentStep(state, step) {
      state.currentStep = step
    },
    setValidationStatus(state, value) {
      state.validationStatus = value
    },
    setErrorMessageList(state, value) {
      state.errorMessageList = value
    },
    setUtmTags(state, { tag, data }) {
      state.utmTags[tag] = { ...state.utmTags[tag], ...data }
    },
    resetUtmTags(state) {
      state.utmTags = { ...utmTags }
    },
    setShowUtmTags(state, value) {
      state.showUtmTags = value
    },
    setUrlPlaceholder(state, value) {
      state.urlPlaceholder = value
    },
  },

  getters: {
    isStoreInGroup:
      ({ selectedStore }) =>
      (id) =>
        caseOf(selectedStore, {
          Done: (store) => some(store.groups, (group) => group.id === id),
          _: () => false,
        }),
    isGroupInTopList:
      ({ selectedStore }) =>
      (group) =>
        caseOf(selectedStore, {
          Done: (store) => some(store.topList, (top) => top.locale === group.key),
          _: () => false,
        }),
    isStoreInCategory:
      ({ selectedStore }) =>
      (id) =>
        caseOf(selectedStore, {
          Done: (store) => some(store.categories, (category) => category.id === id),
          _: () => false,
        }),
    domains:
      ({ selectedStore }) =>
      (group) =>
        caseOf(selectedStore, {
          Done: (store) => some(store.topList, (top) => top.locale === group.key),
          _: () => false,
        }),

    selectedStore: (state) => state.selectedStore,
    storeLogo: (state) => state.storeLogo,
    status: (state) => state.status,
    storeCategories: (state) => state.storeCategories,
    storeProviders: (state) => {
      return state.storeProviders
    },
    feedOrUrl: (state) => state.feedOrUrl,
    paymentTypes: (state) => state.paymentTypes,
    currentStep: (state) => state.currentStep,
    validationStatus: (state) => state.validationStatus,
    errorMessage: ({ errorMessageList }) => (errorMessageList.length ? errorMessageList : 'Something went wrong!'),
    store: () => () => maybe(null),
    groups: (state, getters, rootState, rootGetters) =>
      map(rootGetters['storeGroups/groups'], (groups) => groups.concat({ name: 'unconnected', id: -1 })),
    utmTags: (state) => state.utmTags,
    showUtmTags: (state) => state.showUtmTags,
    getUrlPlaceholder: (state) => (key) => state.providersWithUtmTags[key],
    urlPlaceholder: (state) => state.urlPlaceholder,
    providersWithUtmTags: (state) => state.providersWithUtmTags,
  },

  actions: {
    initStore({ state, commit, dispatch }, isNewStore) {
      dispatch('storeGroups/fetch', {}, { root: true })
      const selectedStore = caseOf(state.selectedStore, {
        Done: (d) => d,
        _: () => '',
      })
      commit('setLogoFile', null)
      let feedOrUrlVal = state.feedOrUrl
      if (isNewStore && !feedOrUrlVal) {
        feedOrUrlVal = 'feed'
        commit('setFeedOrUrl', feedOrUrlVal)
      } else if (!isNewStore && !feedOrUrlVal) {
        if (!selectedStore.url) {
          feedOrUrlVal = 'feed'
        } else if (!selectedStore.feed) {
          feedOrUrlVal = 'url'
        } else {
          feedOrUrlVal = 'both'
        }
        commit('setFeedOrUrl', feedOrUrlVal)
      }

      if (!isNewStore) {
        dispatch('initUtmTags', selectedStore.url)
        commit(
          'setShowUtmTags',
          Object.values(state.utmTags).some((item) => item.value),
        )
      }
    },
    updateCurrentStep({ commit }, step) {
      commit('setCurrentStep', step)
    },
    setStoreProviders({ commit }) {
      httpClient
        .get(`/storeProviders`)
        .then((response) => commit('setStoreProviders', response.data))
        .catch((err) => console.error(err))
    },
    setStoreCategories({ commit }) {
      httpClient
        .get(`/storeCategories`)
        .then((response) => commit('setStoreCategories', response.data))
        .catch((err) => console.error(err))
    },
    toggleTopList({ commit, state }, { group, checked }) {
      map(state.selectedStore, (store) => {
        let topList = store.topList

        if (checked === false) {
          topList = topList.filter((item) => item.locale !== group.key)
        } else {
          if (
            topList.some((item) => {
              return item.locale === group.key
            }) === false
          ) {
            topList.push({
              locale: group.key,
              order: 1,
              toplist: 'retailers',
              key: store.name,
              store_id: store.id,
            })
          }
        }

        commit('setField', { field: 'topList', value: topList })
      })
    },
    toggleGroupConnection({ commit, getters, state }, { group, checked }) {
      map(state.selectedStore, (store) => {
        commit(
          'setSelectedStore',
          HTTP.Request.Done({
            ...store,
            groups: checked
              ? getters.isStoreInGroup(group.id)
                ? store.groups
                : store.groups.concat(group)
              : filter(store.groups, (storeGroup) => storeGroup.id !== group.id),
          }),
        )

        if (checked === false) {
          const topList = store.topList.filter((item) => item.locale !== group.key)
          commit('setField', { field: 'topList', value: topList })
        }
      })
    },
    toggleCategoryConnection({ commit, getters, state }, { storeCategory, checked }) {
      map(state.selectedStore, (store) => {
        commit(
          'setSelectedStore',
          HTTP.Request.Done({
            ...store,
            categories: checked
              ? getters.isStoreInCategory(storeCategory.id)
                ? store.categories
                : store.categories.concat(storeCategory)
              : filter(store.categories, (category) => category.id !== storeCategory.id),
          }),
        )
      })
    },
    selectStore({ commit, dispatch, getters }, { id }) {
      commit('setSelectedStore', HTTP.Request.Pending())

      dispatch('storeGroups/fetch', {}, { root: true })

      const setStore = (store) => {
        commit('setSelectedStore', HTTP.Request.Done(store))
        commit('setOriginalStore', store)
      }

      fold(getters.store(id), setStore, () =>
        httpClient
          .get(`/stores/${id}`)
          .then((response) => {
            commit('setLogo', response.data.logo_url)

            const store = makeStore(response.data)

            setStore({ ...store })
          })
          .catch((error) => commit('setSelectedStore', error)),
      )
    },
    selectEmptyStore({ commit, dispatch }) {
      dispatch('storeGroups/fetch', {}, { root: true })

      commit('setSelectedStore', HTTP.Request.Done(emptyStore))
      commit('setOriginalStore', emptyStore)
    },
    resetStore({ commit, dispatch }) {
      commit('resetStore')
      dispatch('clearWizardForm')
    },
    updateField({ commit }, { field, value }) {
      commit('setField', { field, value })
    },
    saveStore({ dispatch, commit, state }) {
      commit('setStatus', Status.Processing())
      map(state.selectedStore, (store) =>
        httpClient
          .post('/stores', {
            ...serializeStore({ store }),
          })
          .then((response) => {
            const { id } = response.data
            if (!id) {
              return
            }
            if (state.storeLogoFile) {
              const file = state.storeLogoFile
              dispatch('updateStoreLogo', { file, id, isNeedRedirect: true })
            } else {
              commit('setStatus', Status.Saved())
              window.location.href = `${config.adminV2Url}/advertisers/${id}/url-matching`
            }
          })
          .catch(() => commit('setStatus', Status.Failed())),
      )
    },
    updateStore({ dispatch, commit, state }) {
      commit('setStatus', Status.Processing())
      map(state.selectedStore, (store) =>
        httpClient
          .put(`/stores/${store.id}`, {
            ...serializeStore({ store }),
          })
          .then(() => {
            if (state.storeLogoFile) {
              const file = state.storeLogoFile
              const { id } = store
              dispatch('updateStoreLogo', { file, id, isNeedRedirect: true })
            } else {
              commit('setStatus', Status.Updated())
              window.location.href = `${config.adminV2Url}/advertisers/${store.id}/url-matching`
            }
          })
          .catch((error) => {
            if (error.response?.data?.errors) {
              let errors = []
              if (error.response.data.errors.urls) {
                error.response.data.errors.urls.forEach((item) => {
                  errors.push(item.replace('urls', 'domains'))
                })
              } else {
                errors = Object.values(error.response?.data?.errors)
              }
              commit('setErrorMessageList', errors.flat())
            }
            commit('setStatus', Status.Failed())
          }),
      )
    },
    updateStoreLogo({ commit }, { id, file, isNeedRedirect = false }) {
      httpClient
        .post(`/stores/${id}/logo`, file)
        .then((res) => {
          commit('setLogo', res.data.url)
          commit('setStatus', Status.Updated())
          if (isNeedRedirect) {
            window.location.href = `${config.adminV2Url}/advertisers/${id}/url-matching`
          }
        })
        .catch((error) => {
          const message = 'update store logo'
          errorHandler({ error, commit, message })
          commit('setStatus', Status.Failed())
        })
    },
    updateFeedOrUrl({ commit }, value) {
      commit('setFeedOrUrl', value)
    },
    clearWizardForm({ commit }) {
      commit('setCurrentStep', 0)
      commit('setFeedOrUrl', null)
      commit('setEmptyPaymentTypes')
      commit('setValidationStatus', '')
      commit('clearLogo')
    },
    clearValidationStatus({ commit }) {
      commit('setValidationStatus', '')
    },
    isFormValid({ commit, state }) {
      const result = map(state.selectedStore, (store) => {
        const isFormInvalid = !store.name || !store.provider || !store.providerAccountID || !store.url || !store.locale || !store.currency
        if (isFormInvalid) {
          commit('setValidationStatus', 'The form is not filled in correctly.')
          commit('setStatus', Status.Idle())
          return false
        }
        return true
      })
      return caseOf(result, { Done: (result) => result, _: () => '' })
    },
    initUtmTags({ commit }, url) {
      // The (?<=utm_campaign=) is a Regex lookbehind assertion: match anything preceded by 'utm_campaign='
      // The [^&)] is a character class, it matches everything except & and )
      // The .[0] Optional Chaining act as null safety to access the first element
      // of the array so instead of throwing an error it will return undefined.
      const utmCampaign = url.match(/(?<=utm_campaign=)[^&)]*/)?.[0]
      const utmMedium = url.match(/(?<=utm_medium=)[^&)]*/)?.[0]
      const utmSource = url.match(/(?<=utm_source=)[^&)]*/)?.[0]
      const utmContent = url.match(/(?<=utm_content=)[^&)]*/)?.[0]
      const utmTerm = url.match(/(?<=utm_term=)[^&)]*/)?.[0]

      commit('setUtmTags', { tag: 'campaign', data: { value: utmCampaign } })
      commit('setUtmTags', { tag: 'medium', data: { value: utmMedium } })
      commit('setUtmTags', { tag: 'source', data: { value: utmSource } })
      commit('setUtmTags', { tag: 'content', data: { value: utmContent } })
      commit('setUtmTags', { tag: 'term', data: { value: utmTerm } })
    },
  },
}
